import { useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/* ============================================================
   Para que lado a tela entra

   O app trocava de tela sem movimento nenhum: o conteúdo antigo sumia e
   o novo aparecia no mesmo quadro. É o mesmo tipo de detalhe do
   `nativo.ts` — ninguém consegue nomear, a pessoa só sente que está num
   site. Só que aqui não é só aparência: o movimento é o que diz PRA
   ONDE ela foi. Entrar num prato e voltar dele são coisas opostas, e
   sem animação as duas são idênticas na tela.

   Este módulo responde uma pergunta só — entrou, voltou ou trocou de
   lugar? — e devolve a classe que a CSS usa. O desenho do movimento
   está em `styles/app.css`, "a troca de tela".

   POR QUE NÍVEL E NÃO O HISTÓRICO DO NAVEGADOR. O tipo de navegação
   (`POP`/`PUSH`) diz como se chegou, não para onde: quem toca em
   "Meus desafios" dentro de um desafio faz um `PUSH` para a Home, que
   é claramente uma saída. Nível é a hierarquia que o app já desenha na
   barra de cima — a seta de voltar sobe um nível —, então ele acerta
   sem depender de a pessoa ter usado a seta ou o botão do aparelho.
   ============================================================ */

/* As abas de baixo do desafio. Elas são o MESMO nível do feed: trocar de
   aba não é entrar nem voltar, e é por isso que a lista existe em vez de
   contar pedaços do caminho — `/c/:cid` tem dois e `/c/:cid/guia` tem
   três, e as duas telas estão lado a lado para quem usa. */
const ABAS_DO_DESAFIO = new Set(["classificacao", "guia", "detalhes"]);

/**
 * A que profundidade da hierarquia o caminho está.
 *
 * 0 é a Home, 1 são as telas soltas (conta, assinar, sobre), 2 é o
 * desafio com suas abas, e daí para baixo é o que abre de dentro do
 * desafio — prato, recap, perfil, editar.
 */
function nivel(caminho: string): number {
  const pedacos = caminho.split("/").filter(Boolean);
  if (pedacos.length === 0) return 0;
  if (pedacos[0] !== "c") return 1;
  if (pedacos.length <= 2) return 2;
  const aba = pedacos[2];
  if (pedacos.length === 3 && aba && ABAS_DO_DESAFIO.has(aba)) return 2;
  // `/c/:cid/novo` é 3, `/c/:cid/p/:pid` é 4, e o editar do prato é 5.
  return pedacos.length;
}

function direcao(de: string, para: string): string {
  const passo = nivel(para) - nivel(de);
  if (passo > 0) return "tela-frente";
  if (passo < 0) return "tela-tras";
  return "tela-troca";
}

/**
 * A classe de animação da tela que está entrando.
 *
 * Devolve vazio na ABERTURA do app: animar a primeira tela atrasaria o
 * app em 200ms toda vez que ele abre, que é justamente quando a pessoa
 * já esperou o Firebase acordar.
 *
 * `REPLACE` também não ganha direção. Ele é o Guard mandando para o
 * login e o convite trocando de endereço depois de entrar — a pessoa
 * não navegou para lugar nenhum, então deslizar contaria uma história
 * que não aconteceu.
 */
export function useTransicaoDeTela(): string {
  const { pathname } = useLocation();
  const tipo = useNavigationType();

  /* A classe fica GUARDADA junto com o caminho que a gerou, e não é
     recalculada a cada render. Sem isso o StrictMode a apagaria: ele
     renderiza duas vezes em desenvolvimento, e na segunda o caminho
     anterior já seria o atual — a animação sumiria só na máquina de
     quem desenvolve, que é o pior lugar para ela sumir. */
  const visto = useRef<{ caminho: string; classe: string }>({ caminho: pathname, classe: "" });

  if (visto.current.caminho !== pathname) {
    visto.current = {
      caminho: pathname,
      classe: tipo === "REPLACE" ? "tela-troca" : direcao(visto.current.caminho, pathname),
    };
  }

  return visto.current.classe;
}
